import type { FlowGraph } from '@flows/api/blast-api-types';
import { getTemplateByKey } from '@features/flows/palette/registry';
import type { Node, Edge } from '@xyflow/react';
import { computePinsForNodeData, shouldSchemaDrivePins } from '@features/flows/lib/pins';

type Channel = 'exec' | 'data';
type Pins = {
  exec?: { in?: { id: string }[]; out?: { id: string }[] };
  data?: { in?: { id: string }[]; out?: { id: string }[] };
};

const composeHandleId = (channel: Channel, io: 'in' | 'out', rawId: string) =>
  `${channel}:${io}:${rawId}`;

// Parse a maybe-qualified handle like "exec:out:next" or return the raw id
function parseMaybeQualified(
  handle: string | undefined,
): { channel?: Channel; io?: 'in' | 'out'; raw?: string } {
  if (!handle) return {};
  const parts = String(handle).split(':');
  if (parts.length >= 3) {
    const [ch, io, raw] = parts as [Channel, 'in' | 'out', string];
    if ((ch === 'exec' || ch === 'data') && (io === 'in' || io === 'out') && raw) {
      return { channel: ch, io, raw };
    }
  }
  return { raw: String(handle) };
}

const idIn = (arr?: { id: string }[], id?: string) =>
  !!(arr && id && arr.some(p => p.id === id));

const detectChannel = (pins: Pins | undefined, io: 'in' | 'out', rawId: string): Channel | undefined => {
  if (!pins) return undefined;
  if (io === 'in') {
    if (idIn(pins.exec?.in, rawId)) return 'exec';
    if (idIn(pins.data?.in, rawId)) return 'data';
  } else {
    if (idIn(pins.exec?.out, rawId)) return 'exec';
    if (idIn(pins.data?.out, rawId)) return 'data';
  }
  return undefined;
};

function mapNodeType(flowType?: string, data?: Record<string, any>): Node['type'] {
  // Render start nodes with the input style
  if (data?.isStart) return 'input';
  // Explicit output nodes keep output style
  if (flowType === 'output') return 'output';
  // Treat any persisted/custom types (including 'standard') as our default CustomNode
  return 'default';
}

function generateDefaultPosition(index: number, nodeId: string, data?: Record<string, any>): { x: number; y: number } {
  if (data?.isStart) {
    return { x: 0, y: 50 };
  }
  const row = Math.floor(index / 3);
  const col = index % 3;
  return { 
    x: 200 + (col * 250), 
    y: 50 + (row * 150)
  };
}

function extractPins(nodeData?: Record<string, any>) {
  // Persisted pins → reuse; otherwise compute from attrs/bindings.
  if (nodeData?.pins) {
    const tpl = getTemplateByKey(String((nodeData as any)?.templateKey || (nodeData as any)?.handler || ''));
    const schemaDriven = shouldSchemaDrivePins(nodeData);
    const merged = computePinsForNodeData(nodeData, {
      preserveExistingDataIn: true,
      attrSchema: (tpl as any)?.attrsSchema,
      baseDataIn: (tpl as any)?.ports?.data?.in,
      baseDataOut: (tpl as any)?.ports?.data?.out,
      preserveExistingDataOut: !schemaDriven,
    });
    // special-case: switch keeps its custom exec outs
    if (nodeData?.type === 'switch' && nodeData?.pins?.exec?.out) {
      merged.exec = { ...(merged.exec ?? {}), out: nodeData.pins.exec.out };
    }
    // Keep data pins for start nodes; optionally strip only for explicit 'end' nodes
    if (nodeData?.type === 'end') {
      delete (merged as any).data;
    }
    return merged;
  }
  const tpl = getTemplateByKey(String((nodeData as any)?.templateKey || (nodeData as any)?.handler || ''));
  const schemaDriven = shouldSchemaDrivePins(nodeData);
  const computed = computePinsForNodeData(nodeData ?? {}, {
    preserveExistingDataIn: true,
    attrSchema: (tpl as any)?.attrsSchema,
    baseDataIn: (tpl as any)?.ports?.data?.in,
    baseDataOut: (tpl as any)?.ports?.data?.out,
    preserveExistingDataOut: !schemaDriven,
  });
  if (nodeData?.type === 'end') {
    delete (computed as any).data;
  }
  return computed;
}

export function adaptFlowGraph(graph: FlowGraph): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = graph.nodes.map((n, index) => {
    const data = n.data ?? {};
    // Derive handler fields for runtime (and future saves) if missing
    const handler: string =
      (data as any).handler ??
      (data as any).templateKey ??
      (typeof n.type === 'string' ? n.type : undefined) ??
      'custom.node';
    // Prefer version from node data, else from palette template, else from attrs._version, else default
    let inferredVersion: string | undefined =
      (typeof (data as any).handlerVersion === 'string' ? (data as any).handlerVersion : undefined) ||
      (typeof (data as any).version === 'string' ? (data as any).version : undefined);
    if (!inferredVersion) {
      const key = (data as any).templateKey || handler;
      if (key) {
        const tpl = getTemplateByKey(String(key));
        if (tpl?.version) inferredVersion = tpl.version;
      }
    }
    if (!inferredVersion) {
      const av = (data as any).attrs?._version;
      if (typeof av === 'string') inferredVersion = av;
      else if (typeof av === 'number') inferredVersion = String(av);
    }
    if (!inferredVersion) inferredVersion = '1.0.0';

    return {
      id: n.id,
      type: mapNodeType(n.type, data),
      data: {
        label: (data as any).label ?? n.id,
        ...data,
        // Ensure these are present in the in-memory graph
        handler,
        handlerVersion: inferredVersion,
        pins: extractPins(data),
        // Preserve the originally persisted node type so we can round-trip it on save
        persistedType: typeof n.type === 'string' ? (n.type as string) : undefined,
      },
      position: n.position ? { x: n.position.x, y: n.position.y } : generateDefaultPosition(index, n.id, data),
    };
  });

  const nodePinsMap = new Map<string, Pins>();
  nodes.forEach(node => {
    const pins = node.data.pins as Pins;
    if (pins) {
      nodePinsMap.set(node.id, pins);
    }
  });

  const nodeIds = new Set(nodes.map(n => n.id));

  const edges: Edge[] = graph.edges
    .filter(e => {
      if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) {
        console.warn(`Skipping edge ${e.id}: references non-existent node(s)`, e);
        return false;
      }
      return true;
    })
    .map(e => {
      const srcPins = nodePinsMap.get(e.source);
      const tgtPins = nodePinsMap.get(e.target);

      // Prefer explicitly provided raw handles in data, else parse from the qualified handle, else fall back
      const parsedSrc = parseMaybeQualified(e.sourceHandle);
      const parsedTgt = parseMaybeQualified(e.targetHandle);
      const rawSrc = (e as any).data?.rawSourceHandle ?? parsedSrc.raw ?? 'out';
      const rawTgt = (e as any).data?.rawTargetHandle ?? parsedTgt.raw ?? 'in';

      // Detect/resolve channel
      const srcChannel = parsedSrc.channel ?? detectChannel(srcPins, 'out', rawSrc!);
      const tgtChannel = parsedTgt.channel ?? detectChannel(tgtPins, 'in', rawTgt!);
      const channel = (srcChannel ?? tgtChannel ?? (e as any).data?.channel ?? 'exec') as Channel;

      if (!srcChannel && !parsedSrc.channel) {
        console.warn(`Edge ${e.id}: source handle '${rawSrc}' not found in node ${e.source} pins`, srcPins);
      }
      if (!tgtChannel && !parsedTgt.channel) {
        console.warn(`Edge ${e.id}: target handle '${rawTgt}' not found in node ${e.target} pins`, tgtPins);
      }

      // Preserve existing fully-qualified handles when possible to avoid double-prefixing
      const sourceHandle = parsedSrc.channel ? (e.sourceHandle as string) : composeHandleId(channel, 'out', rawSrc!);
      const targetHandle = parsedTgt.channel ? (e.targetHandle as string) : composeHandleId(channel, 'in', rawTgt!);

      return {
        id: e.id || `${e.source}-${e.target}-${rawSrc}-${rawTgt}`,
        source: e.source,
        target: e.target,
        sourceHandle,
        targetHandle,
        type: channel === 'data' ? 'data' : 'exec',
        style: channel === 'exec' ? {
          strokeWidth: 3,
          stroke: '#ffffff',
        } : {
          strokeWidth: 2,
          stroke: '#3b82f6',
        },
        data: {
          ...(e as any).data,
          channel,
          rawSourceHandle: rawSrc,
          rawTargetHandle: rawTgt,
        }
      } as any;
    });

  return { nodes, edges };
}

// Convert React Flow graph back to persisted FlowGraph format expected by backend
export function toFlowGraph(nodes: Node[], edges: Edge[]): FlowGraph {
  const parse = (
    handle?: string | null,
  ): { channel?: Channel; io?: 'in' | 'out'; raw?: string } => {
    if (!handle) return {};
    const parts = String(handle).split(':');
    if (parts.length >= 3) {
      const [ch, io, raw] = parts as [Channel, 'in' | 'out', string];
      if ((ch === 'exec' || ch === 'data') && (io === 'in' || io === 'out') && raw) return { channel: ch, io, raw };
    }
    return { raw: String(handle) };
  };

  const outNodes = nodes.map((n) => {
    const d = (n.data as any) ?? {};
    // Derive handler and version for persistence
    const handler: string =
      (d.handler as string) ??
      (d.templateKey as string) ??
      (typeof d.type === 'string' ? (d.type as string) : undefined) ??
      (typeof n.type === 'string' ? (n.type as string) : 'custom.node');
    // Prefer template version when available to avoid numeric-only versions (e.g., 1 → "1.0.0")
    let handlerVersion: string | undefined =
      (typeof d.handlerVersion === 'string' ? d.handlerVersion : undefined) ||
      (typeof d.version === 'string' ? d.version : undefined);
    if (!handlerVersion) {
      const key = (d.templateKey as string) || (d.handler as string) || undefined;
      if (key) {
        const tpl = getTemplateByKey(String(key));
        if (tpl?.version) handlerVersion = tpl.version;
      }
    }
    if (!handlerVersion) {
      const av = d.attrs?._version as any;
      if (typeof av === 'string') handlerVersion = av;
      else if (typeof av === 'number') handlerVersion = String(av);
    }
    if (!handlerVersion) handlerVersion = '1.0.0';

    const outData = { ...d, handler, handlerVersion };

    // Persist a normalized node type: prefer stored persistedType, else template-provided type, else 'standard'
    let persistedType: string | undefined = typeof d.persistedType === 'string' ? d.persistedType : undefined;
    if (!persistedType) {
      const key = (d.templateKey as string) || (d.handler as string) || undefined;
      if (key) {
        const tpl = getTemplateByKey(String(key));
        if (tpl?.nodeType && typeof tpl.nodeType === 'string') persistedType = tpl.nodeType;
      }
    }
    if (!persistedType) persistedType = 'standard';

    return {
      id: n.id,
      type: persistedType,
      data: outData,
      position: n.position ? { x: n.position.x, y: n.position.y } : undefined,
    };
  });

  const outEdges = edges.map((e) => {
    const looksData =
      (typeof (e as any).sourceHandle === 'string' && String((e as any).sourceHandle).startsWith('data:')) ||
      (typeof (e as any).targetHandle === 'string' && String((e as any).targetHandle).startsWith('data:'));
    const parsedS = parse(e.sourceHandle as any);
    const parsedT = parse(e.targetHandle as any);
    const rawSource = (e as any).data?.rawSourceHandle ?? parsedS.raw ?? 'out';
    const rawTarget = (e as any).data?.rawTargetHandle ?? parsedT.raw ?? 'in';
    const channel: Channel = ((e as any).data?.channel ?? parsedS.channel ?? parsedT.channel ?? (looksData ? 'data' : 'exec')) as Channel;
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: rawSource,
      targetHandle: rawTarget,
      data: { ...(e as any).data, channel },
    };
  });

  return { nodes: outNodes as any, edges: outEdges as any };
}
